import { defineStore } from 'pinia';

let nextToastId = 1;

// Fallback when a page hasn't supplied a specific title (rare — the
// router's meta.title covers every routed page). Plain so it doesn't
// hijack precious tab-strip real estate.
const TITLE_FALLBACK = 'Portal';

/** Lightweight UI state: breadcrumbs + toast notifications. */
export const useUiStore = defineStore('ui', {
    state: () => ({
        // null = use the auto-derived crumbs from $route; otherwise pages override.
        crumbs: null,
        // Optionaler Annotations-Text rechts neben dem Page-Titel im
        // Topbar (z.B. "heute · 3 h 45 min" auf /cases). Seite setzt
        // ihn in `mounted` und leert in `beforeUnmount`, damit beim
        // Routenwechsel keine veraltete Info hängenbleibt.
        titleAnnotation: null,
        // The customer the operator is currently looking at. Pages
        // with a customer in scope (CustomerDetail, CaseDetail,
        // BillDetail, ContractDetail) set this on mount / data load
        // and clear on unmount. Read by global shortcuts (quick_case,
        // quick_note) so "Neuer Supportfall" lands on the obvious
        // customer instead of throwing "Kunde fehlt".
        // Shape: { id: number, label: string } | null.
        currentCustomer: null,
        // [{ id, type: 'success'|'error'|'warn'|'info', title?, message, sticky,
        //    action?: { label, onClick } }]
        // `action` makes the toast actionable — surfaces a button next
        // to the dismiss X that fires onClick (then auto-dismisses the
        // toast). Used for "Undo"-style recovery after destructive
        // actions: cancel a bill → toast offers Wiederherstellen for
        // ~8 seconds. The handler is stored as a plain function in
        // Pinia state — fine here, the state isn't persisted.
        toasts: [],
    }),
    actions: {
        setCrumbs(crumbs) { this.crumbs = crumbs; },
        clearCrumbs()      { this.crumbs = null; },
        setTitleAnnotation(text) { this.titleAnnotation = text || null; },
        clearTitleAnnotation()    { this.titleAnnotation = null; },

        // Set the browser tab / history-entry title. Whatever the
        // caller passes becomes the entire title — no brand suffix
        // is appended (the brand wastes tab-strip space and forces
        // the page-specific part to truncate first; operators want
        // "B01129 · Nydegger AG" visible, not "B01129 · Nydegger A…").
        //
        // Two entry points:
        //   • router.afterEach in app.js calls this with the route's
        //     `meta.title` so EVERY page lands with a sensible title
        //     immediately on navigation.
        //   • Detail pages call it again from their data-load handler
        //     (next to setCrumbs) to upgrade the generic "Rechnung"
        //     to the specific "B01129 · Rechnung" once the resource
        //     is in hand. Browser history then records the specific
        //     entry — meaningful in back/forward navigation.
        setDocTitle(title) {
            const t = String(title || '').trim();
            document.title = t || TITLE_FALLBACK;
        },
        setCurrentCustomer(c) {
            this.currentCustomer = c?.id ? { id: Number(c.id), label: String(c.label ?? '') } : null;
        },
        clearCurrentCustomer() { this.currentCustomer = null; },

        // Toast helpers
        toast({ type = 'info', title = '', message = '', sticky = false, ttl, action } = {}) {
            const id = nextToastId++;
            this.toasts.push({ id, type, title, message, sticky, action: action || null });
            // Errors stick by default; others auto-dismiss. Actionable
            // toasts get a longer default TTL (8 s vs 3.5 s) so the
            // operator has time to realize they want to undo. Caller
            // can still override with explicit `ttl`.
            const fallback = type === 'error' ? 8000 : (action ? 8000 : 3500);
            const timeout = ttl ?? fallback;
            if (!sticky) setTimeout(() => this.dismissToast(id), timeout);
            return id;
        },
        success(message, opts = {}) { return this.toast({ ...opts, type: 'success', message }); },
        info(message, opts = {})    { return this.toast({ ...opts, type: 'info', message }); },
        warn(message, opts = {})    { return this.toast({ ...opts, type: 'warn', message }); },
        error(message, opts = {})   { return this.toast({ ...opts, type: 'error', message }); },
        dismissToast(id) { this.toasts = this.toasts.filter(t => t.id !== id); },
        // Invoke an actionable toast's handler + dismiss. Wrapped so
        // the template can call one method without juggling the
        // toast object's id alongside the action object.
        invokeToastAction(id) {
            const t = this.toasts.find(x => x.id === id);
            if (t?.action?.onClick) {
                try { t.action.onClick(); }
                finally { this.dismissToast(id); }
            }
        },
    },
});
